import * as React from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import { toast } from "react-toastify";

const columns: GridColDef[] = [
  {
    field: "id",
    headerName: "Order Id",
    flex: 1,
    align: "center",
    headerAlign: "center",
    valueGetter: (params) => {
      const idStr = params.row.id?.toString() || "";
      return "#" + idStr.slice(-6);
    },
  },
  {
    field: "totalRemainingAmount",
    headerName: "Full Amounts",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "withdrawableAmount",
    headerName: "Withdrawable Amount",
    flex: 2,
    align: "center",
    headerAlign: "center",
    valueGetter: (params) => {
      const total = Number(params.row.totalRemainingAmount) || 0;
      const fees = Number(params.row.totalTransactionFees) || 0;
      const withdrawable = total - fees;
      const withdrawableRounded = Math.round(withdrawable * 100) / 100;
      return withdrawableRounded.toFixed(2);
    },
  },
  {
    field: "totalTransactionFees",
    headerName: "Total Transaction Fees",
    flex: 2,
    align: "center",
    headerAlign: "center",
    valueFormatter: (param) => {
      if (param.value == null) return "";
      return Number(param.value).toFixed(2);
    },
  },
  {
    field: "remainingTipsAmount",
    headerName: "Total Tips",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "totalVat",
    headerName: "Total Vat",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "totalSd",
    headerName: "Total Sd",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "remainingServiceChargeAmount",
    headerName: "Total Service Charge",
    sortable: false,
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "remainingServiceChargeVatAmount",
    headerName: "Total Service Charge VAT",
    sortable: false,
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "orderDate",
    headerName: "Order Date",
    sortable: false,
    flex: 2,
    align: "center",
    headerAlign: "center",
    valueFormatter: (params) => {
      if (!params.value) return "";
      const date = new Date(params.value);
      if (isNaN(date.getTime())) return params.value;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      const hoursStr = String(hours).padStart(2, "0");
      return `${year}-${month}-${day} ${hoursStr}:${minutes} ${ampm}`;
    },
  },
  {
    field: "status",
    headerName: "Status",
    flex: 1,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => {
      const orderDate = new Date(params.row.orderDate);
      const now = new Date();
      const diffMs = now.getTime() - orderDate.getTime();
      const diffHours = diffMs / (1000 * 60 * 60);
      const isActive = diffHours > 24;
      return (
        <span style={{ color: isActive ? "green" : "red", fontWeight: 600 }}>
          {isActive ? "Active" : "Pending"}
        </span>
      );
    },
  },
];

export default function WithdrawDataTable(props) {
  const [rows, setRows] = React.useState<any[]>([]);
  const [allRows, setAllRows] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [rowSelectionModel, setRowSelectionModel] = React.useState<any[]>([]);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 5,
  });

  // Helper function to calculate withdrawable amount consistently
  const calculateWithdrawableAmount = (order: any): number => {
    const totalAmount = Number(order.totalRemainingAmount) || 0;
    const transactionFees = Number(order.totalTransactionFees) || 0;
    const withdrawable = totalAmount - transactionFees;
    // Round to 2 decimal places to handle floating-point precision issues
    return Math.round(withdrawable * 100) / 100;
  };

  // Validation function to check if order can be selected
  const canSelectOrder = (order: any): boolean => {
    const totalAmount = Number(order.totalRemainingAmount) || 0;
    const transactionFees = Number(order.totalTransactionFees) || 0;
    const withdrawableAmount = calculateWithdrawableAmount(order);

    // Check if Total Amount is negative
    if (totalAmount < 0) {
      return false;
    }

    // Check if Withdrawable Amount is 0.00 or less (after rounding to 2 decimal places)
    if (withdrawableAmount <= 0) {
      return false;
    }

    return true;
  };

  // Handle row selection with validation
  const handleRowSelectionChange = (newRowSelectionModel: any[]) => {
    const invalidOrders: any[] = [];
    const validOrders: any[] = [];

    // Check each selected order for validation
    newRowSelectionModel.forEach((orderId) => {
      const order = allRows.find((row) => row.id === orderId);
      if (order) {
        if (!canSelectOrder(order)) {
          invalidOrders.push(order);
        } else {
          validOrders.push(order);
        }
      }
    });

    // If there are invalid orders, show toast and prevent selection
    if (invalidOrders.length > 0) {
      const orderIds = invalidOrders
        .map((order) => `#${order.id.toString().slice(-6)}`)
        .join(", ");
      const message = `You cannot select the following orders: ${orderIds}. Total Amount / Withdrawable Amount is negative or 0.00.`;

      // Show toast message
      toast.error(message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });

      // Force unselect invalid orders by setting only valid orders
      const validOrderIds = validOrders.map((order) => order.id);

      // Update the selection model to only include valid orders
      setRowSelectionModel(validOrderIds);

      // Update the parent component with only valid orders
      props.selectOrder(validOrders);

      // Force a re-render to ensure the UI updates
      setTimeout(() => {
        setRowSelectionModel((prev) => [...prev]); // Force re-render
      }, 100);

      return;
    }

    // All orders are valid, proceed with selection
    setRowSelectionModel(newRowSelectionModel);
    const selectedRows = allRows.filter((row) =>
      newRowSelectionModel.includes(row.id)
    );
    props.selectOrder(selectedRows);
  };

  React.useEffect(() => {
    console.log(props.tableData);
    setAllRows(props.tableData);
  }, []);

  React.useEffect(() => {
    if (allRows.length > 0) {
      const start = paginationModel.page * paginationModel.pageSize;
      const end = start + paginationModel.pageSize;
      const pagedRows = allRows.slice(start, end);
      setRows(pagedRows);

      // Validate any existing selections when data loads
      if (rowSelectionModel.length > 0) {
        const validSelections = rowSelectionModel.filter((orderId) => {
          const order = allRows.find((row) => row.id === orderId);
          return order && canSelectOrder(order);
        });

        if (validSelections.length !== rowSelectionModel.length) {
          setRowSelectionModel(validSelections);
          const validOrders = allRows.filter((row) =>
            validSelections.includes(row.id)
          );
          props.selectOrder(validOrders);
        }
      }
    }
  }, [paginationModel, allRows]);

  React.useEffect(() => {
    if (rowSelectionModel.length > 0) {
      const selectedRows = allRows.filter((row) =>
        rowSelectionModel.includes(row.id)
      );
      props.selectOrder(selectedRows);
    } else {
      props.selectOrder([]);
      console.log("No rows selected");
    }
  }, [rowSelectionModel]);

  return (
    <Paper sx={{ height: 400, width: "100%" }}>
      {allRows.length > 0 && (
        <DataGrid
          key={`datagrid-${rowSelectionModel.length}`}
          rows={rows}
          columns={columns}
          rowCount={allRows.length}
          paginationMode="server"
          paginationModel={paginationModel}
          pageSizeOptions={[5, 10]}
          checkboxSelection
          loading={loading}
          sx={{ border: 0 }}
          rowSelectionModel={rowSelectionModel}
          isRowSelectable={(params) => {
            const orderDate = new Date(params.row.orderDate);
            const now = new Date();
            const diffInHours =
              (now.getTime() - orderDate.getTime()) / (1000 * 60 * 60);

            if (diffInHours < 24) {
              return false;
            }

            const withdrawableAmount =
              params.row.totalRemainingAmount - params.row.totalTransactionFees;
            if (withdrawableAmount <= 0) {
              return false;
            }

            return true;
          }}
          onRowSelectionModelChange={handleRowSelectionChange}
          onPaginationModelChange={(model) => {
            setLoading(true);
            setPaginationModel(model);
            setTimeout(() => {
              setLoading(false);
            }, 500);
          }}
        />
      )}
    </Paper>
  );
}
